import { trackEvent } from "@intercom/messenger-js-sdk";
import { useEffect, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Center,
  Flex,
  Loader,
  Menu,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
  IconDotsVertical,
  IconDownload,
  IconSparkles,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { getUrl } from "aws-amplify/storage";
import {
  GraphQLQuery,
  GraphQLSubscription,
  generateClient,
  put,
} from "aws-amplify/api";
import { mdiContentCopy } from "@mdi/js";
import { Icon } from "@mdi/react";
import QuickRecordConfirmDeleteModal from "@/components/QuickRecord/ConfirmDeleteModal";
import QuickRecordUploadFailedModal from "@/components/QuickRecord/UploadFailedModal";
import CustomAudioPlayer from "@/components/Record/CustomAudioPlayer";
import useUploadRecording from "@/hooks/useUploadRecording";
import { COLORS } from "@/utils/GlobalStyles";
import { useUserProvider } from "@/utils/UserProvider";
import { DatabaseMigrationAdapter } from "@/services/migration";
import {
  appShowNotification,
  getLocalStorageItemsWithPrefix,
  sortByCreateDate,
} from "@/utils/utils";
import { AudioDatabase, getAudioBlobFromIndexedDB } from "@/utils/utils";
import { deleteRecording } from "@/graphql/mutations";
import { recordingsByCID } from "@/graphql/queries";
import {
  onCreateRecording,
  onDeleteRecording,
  onUpdateRecording,
} from "@/graphql/subscriptions";
import {
  DeleteRecordingMutation,
  DeleteRecordingMutationVariables,
  OnCreateRecordingSubscription,
  OnDeleteRecordingSubscription,
  OnUpdateRecordingSubscription,
  RecordingsByCIDQuery,
  RecordingsByCIDQueryVariables,
} from "@/API";


const client = generateClient();



interface tabPanelRecordingsProps {
  caseDetail: any;
  details: any;
  patientId: string;
}

const TabPanelRecordings = ({ caseDetail }: tabPanelRecordingsProps) => {
  const clipboard = useClipboard();
  const { amplifyUser } = useUserProvider();
  const [recordings, setRecordings] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const { handleUploadRecording } = useUploadRecording();
  const [uploadingFailedRecording, setUploadingFailedRecording] =
    useState<boolean>(false);
  const [failedRecordingToUpload, setFailedRecordingToUpload] =
    useState<any>(null);
  


  useEffect(() => {
    fetchData(false);
  }, []);

  useEffect(() => {
    // Simple subscriptions for UI updates only (no Supabase sync)
    createSubAddRecording();
    createSubUpdateRecording();
    createSubDeleteRecording();
  }, []);

  const createSubAddRecording = () => {
    const createSub = client
      .graphql<GraphQLSubscription<OnCreateRecordingSubscription>>({
        query: onCreateRecording,
      })
      .subscribe({
        next: async ({ data }) => {
          const newRecording = data.onCreateRecording;
          trackEvent("create_recording");
          fetchData(true);
        },
        error: (error) => console.warn(error),
      });
    return () => {
      createSub.unsubscribe();
    };
  };

  const createSubUpdateRecording = () => {
    const updateSub = client
      .graphql<GraphQLSubscription<OnUpdateRecordingSubscription>>({
        query: onUpdateRecording,
      })
      .subscribe({
        next: async ({ data }) => {
          const updatedRecording = data.onUpdateRecording;
          fetchData(true);
          // Upsert to Supabase when a transcription arrives
          if (updatedRecording?.text && updatedRecording.text.length > 0) {
            try {
              const migrationAdapter = DatabaseMigrationAdapter.getInstance();
              await migrationAdapter.upsertRecordingOnView(
                updatedRecording.id,
                updatedRecording as unknown as Record<string, unknown>
              );
            } catch (_) {
              // ignore migration errors in UI flow
            }
          }
        },
        error: (error) => console.warn(error),
      });
    return () => {
      updateSub.unsubscribe();
    };
  };



  const createSubDeleteRecording = () => {
    const deleteSub = client
      .graphql<GraphQLSubscription<OnDeleteRecordingSubscription>>({
        query: onDeleteRecording,
      })
      .subscribe({
        next: () => {
          fetchData(true);
        },
        error: (error) => console.warn(error),
      });
    return () => {
      deleteSub.unsubscribe();
    };
  };

  const fetchData = async (silent = false) => {
    !silent && setLoading(true);
    try {
      const recordingQueryInput: RecordingsByCIDQueryVariables = {
        cID: caseDetail?.id,
      };
      const recordingData = await client.graphql<
        GraphQLQuery<RecordingsByCIDQuery>
      >({
        query: recordingsByCID,
        variables: recordingQueryInput,
      });
      let listData = recordingData?.data?.recordingsByCID?.items || [];
      const updatedListData: any = await Promise.all(
        listData.map(async (item) => {
          if (item?.storagePath?.includes("private")) {
            try {
              const file = (
                await getUrl({
                  path: item?.storagePath,
                  options: {
                    validateObjectExistence: true,
                  },
                })
              ).url.toString();
              return {
                ...item,
                file: file,
                date: item.createdAt,
              };
            } catch (error) {
              return {
                ...item,
                date: item.createdAt,
              };
            }
          } else {
            return { ...item, date: item?.createdAt };
          }
        }),
      );

      // Batch upsert any recordings that already have a transcription
      try {
        const migrationAdapter = DatabaseMigrationAdapter.getInstance();
        const toUpsert = updatedListData
          .filter((r: any) => r?.text && r.text.length > 0)
          .map((r: any) => ({ id: r.id, data: r as Record<string, unknown> }));
        if (toUpsert.length > 0) {
          migrationAdapter.batchUpsertRecordingsOnView(toUpsert);
        }
      } catch (_) {
        // ignore migration errors in UI flow
      }

      const localRecordingsFailedToUploadWithFile =
        await getLocalUploadFailedRecordings();
      const allRecordings = [...localRecordingsFailedToUploadWithFile, ...updatedListData].sort(
        sortByCreateDate,
      );
      setRecordings(allRecordings);
    } catch (error) {
      // Error fetching recordings
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    setRecordings([
      ...recordings.filter((item: any) => item.id !== selectedRecord.id),
    ]);
    const varsDeleteRecording: DeleteRecordingMutationVariables = {
      input: {
        id: selectedRecord?.id,
        _version: selectedRecord._version,
      },
    };
    client.graphql<GraphQLQuery<DeleteRecordingMutation>>({
      query: deleteRecording,
      variables: varsDeleteRecording,
    });
    setSelectedRecord(null);
  };

  const handleSummarizeRecording = async (recordingId: string) => {
    try {
      // const user: GetCurrentUserOutput = await getCurrentUser();
      // const { identityId } = await fetchAuthSession();
      const init = {
        response: false,
        headers: {},
        body: {
          // username: user.username,
          // userIdentityId: identityId,
        },
      };

      const summarizeResult = put({
        apiName: "SummarizeRecording",
        path: `/summarizerecording/${recordingId}`,
        options: init,
      });
      console.log(await summarizeResult.response);
    } catch (error) {
      console.warn(error);
    }
    // await fetchData();
  };

  const getLocalUploadFailedRecordings = async () => {
    const localRecordings = getLocalStorageItemsWithPrefix(
      `${AudioDatabase.PREFIX_RECORD}${caseDetail?.id}`,
    );
    const localRecordingsFailedToUploadWithFile = await Promise.all(
      localRecordings
        ?.filter((item: any) => item.failedToUpload === true && item.file)
        .map(async (item: any) => {
          const recordItem: any = await getAudioBlobFromIndexedDB(
            `${AudioDatabase.PREFIX_RECORD}${caseDetail?.id}-${item.name}`,
          );
          if (recordItem) {
            return {
              ...item,
              file: URL.createObjectURL(recordItem.blob),
              blob: recordItem.blob,
            };
          }
          return null;
        }),
    );
    return localRecordingsFailedToUploadWithFile.filter(
      (item: any) => item !== null,
    );
  };

  const handleUploadFailedRecording = async (recording: any) => {
    try {
      setUploadingFailedRecording(true);
      const uploaded = await handleUploadRecording(
        recording,
        amplifyUser.audioRetentionPeriod,
      );
      if (!uploaded) {
        setFailedRecordingToUpload(recording);
        return;
      }
      setFailedRecordingToUpload(null);
      fetchData(false);
      appShowNotification({
        type: "success",
        title: "Recording successfully uploaded!",
      });
      return;
    } catch (error) {
      // Error uploading failed recording
    } finally {
      setUploadingFailedRecording(false);
    }
  };

  const renderMenu = (recording: any) => {
    if (recording.failedToUpload) {
      return (
        <ActionIcon
          variant="subtle"
          loading={uploadingFailedRecording}
          disabled={uploadingFailedRecording}
          onClick={() => handleUploadFailedRecording(recording)}
        >
          <IconUpload size={"20"} color={COLORS.primary} />
        </ActionIcon>
      );
    }
    return (
      <Flex align={"center"}>
        <Menu
          position="bottom-end"
          shadow="md"
          withArrow
          radius={"sm"}
          offset={-5}
          arrowOffset={10}
        >
          <Menu.Target>
            <Tooltip withArrow label="Menu">
              <ActionIcon variant="subtle">
                <IconDotsVertical size={"20"} color="black" />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<Icon path={mdiContentCopy} size={0.8} />}
              onClick={() => {
                clipboard.copy(recording.text);
                appShowNotification({
                  title: "Copied text to clipboard",
                  type: "success",
                });
              }}
            >
              {"Copy text"}
            </Menu.Item>
            {recording.summary && (
              <Menu.Item
                leftSection={<Icon path={mdiContentCopy} size={0.8} />}
                onClick={() => {
                  clipboard.copy(recording.summary);
                  appShowNotification({
                    title: "Copied summary to clipboard",
                    type: "success",
                  });
                }}
              >
                {"Copy summary"}
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconSparkles size={20} />}
              onClick={() => {
                handleSummarizeRecording(recording.id);
              }}
            >
              {"Summarize"}
            </Menu.Item>
            <Menu.Item leftSection={<IconDownload size={20} />}>
              <Anchor
                href={recording.file}
                download={recording.name}
                variant="subtle"
              >
                <Text fz={14} c={"black"}>
                  Download
                </Text>
              </Anchor>
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              c={"red"}
              leftSection={<IconTrash size={20} />}
              onClick={() => {
                setSelectedRecord(recording);
              }}
            >
              {"Delete"}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    );
  };

  const renderLoader = () => {
    return (
      <Flex h={50} mb={30} w={"100%"} align={"center"} justify={"center"}>
        <Loader className="loading-icon" size={"md"} type="dots" />
      </Flex>
    );
  };

  return (
    <Stack pb={20}>
      {!loading && recordings.length === 0 && (
        <Center>
          <Text>No case recordings yet. Tap "Record" to create one!</Text>
        </Center>
      )}
      {loading && renderLoader()}
      {recordings?.map((recordItem: any, index: number) => (
        <CustomAudioPlayer
          key={index}
          recordItem={recordItem}
          renderActions={() => renderMenu(recordItem)}
        />
      ))}
      <QuickRecordConfirmDeleteModal
        modalOpened={selectedRecord !== null}
        setModalOpened={setSelectedRecord}
        onConfirm={handleDeleteRecord}
        deleting={false}
        title="Do you want to delete this recording?"
        description="This will delete all associated data and CANNOT be undone"
      />
      <QuickRecordUploadFailedModal
        modalOpened={!!failedRecordingToUpload}
        onCancel={() => setFailedRecordingToUpload(null)}
        onTryAgain={() => {
          setFailedRecordingToUpload(null);
          setTimeout(() => {
            handleUploadFailedRecording(failedRecordingToUpload);
          }, 1000);
        }}
      />
    </Stack>
  );
};

export default TabPanelRecordings;

